import { useState, useCallback, useEffect } from 'react';

/**
 * Hook to manage panel collapse states and related modal open logic
 * Handles the interaction between VTO/Manual panels and modal opening
 */
function useMyClosetPanels({
  isVirtualMode,
  droppedItems,
  selectedItem,
  multiSelectedItems,
  setSelectedItem,
  setMultiSelectedItems,
  setIsVirtualMode,
  setIsPersonImageModalOpen,
  setIsBackgroundModalOpen,
  setIsLoginModalOpen
}) {
  // Track panel collapsed states
  const [isVTOPanelCollapsed, setIsVTOPanelCollapsed] = useState(false);
  const [isManualPanelCollapsed, setIsManualPanelCollapsed] = useState(false);

  // Unified controls state - start collapsed by default
  const [isControlsCollapsed, setIsControlsCollapsed] = useState(true);

  // Switch to manual mode and open panel when item is selected
  useEffect(() => {
    if (selectedItem !== null || multiSelectedItems.length > 0) {
      // Switch to manual mode if in VTO mode
      // if (isVirtualMode) {
      //   setIsVirtualMode(false);
      // }
      // Open manual panel
      // setIsManualPanelCollapsed(false);
    }
  }, [selectedItem, multiSelectedItems, isVirtualMode, setIsVirtualMode]);

  // Handler for person image modal - collapses panels first if open
  const handlePersonImageModalOpen = useCallback(() => {
    const isManualPanelOpen = !isVirtualMode && droppedItems.length > 0 && !isManualPanelCollapsed;
    const isVTOPanelOpen = isVirtualMode && !isVTOPanelCollapsed;

    // If any panel is open, collapse it instead of opening modal
    if (isVTOPanelOpen) {
      setIsVTOPanelCollapsed(true);
      return;
    }

    if (isManualPanelOpen) {
      setIsManualPanelCollapsed(true);
      return;
    }

    // Only open modal if nothing is selected and no panels are open
    if (selectedItem === null && multiSelectedItems.length === 0) {
      setIsPersonImageModalOpen(true);
    } else {
      setSelectedItem(null);
      setMultiSelectedItems([]);
    }
  }, [
    isVirtualMode,
    droppedItems,
    isManualPanelCollapsed,
    isVTOPanelCollapsed,
    selectedItem,
    multiSelectedItems,
    setSelectedItem,
    setMultiSelectedItems,
    setIsPersonImageModalOpen
  ]);

  // Handler for background modal - collapses panels first if open
  const handleBackgroundModalOpen = useCallback(() => {
    const isManualPanelOpen = !isVirtualMode && droppedItems.length > 0 && !isManualPanelCollapsed;
    const isVTOPanelOpen = isVirtualMode && !isVTOPanelCollapsed;

    // If any panel is open, collapse it instead of opening modal
    if (isVTOPanelOpen) {
      setIsVTOPanelCollapsed(true);
      return;
    }

    if (isManualPanelOpen) {
      setIsManualPanelCollapsed(true);
      return;
    }

    // Only open modal if nothing is selected and no panels are open
    if (selectedItem === null && multiSelectedItems.length === 0) {
      setIsBackgroundModalOpen(true);
    } else {
      setSelectedItem(null);
      setMultiSelectedItems([]);
    }
  }, [
    isVirtualMode,
    droppedItems,
    isManualPanelCollapsed,
    isVTOPanelCollapsed,
    selectedItem,
    multiSelectedItems,
    setSelectedItem,
    setMultiSelectedItems,
    setIsBackgroundModalOpen
  ]);

  // Handler for login required - opens login modal or clears selection
  const handleLoginRequired = useCallback(() => {
    if (selectedItem === null && multiSelectedItems.length === 0) {
      setIsLoginModalOpen(true);
    } else {
      setSelectedItem(null);
      setMultiSelectedItems([]);
    }
  }, [selectedItem, multiSelectedItems, setSelectedItem, setMultiSelectedItems, setIsLoginModalOpen]);

  // Handler specifically for VTO panel wearer image - opens modal directly
  const handleVTOWearerClick = useCallback(() => {
    setIsPersonImageModalOpen(true);
  }, [setIsPersonImageModalOpen]);

  // Handler for toolbar icon — opens person modal directly without collapsing VTO
  const handleDirectPersonModalOpen = useCallback(() => {
    setIsPersonImageModalOpen(true);
  }, [setIsPersonImageModalOpen]);

  // Handler for toolbar icon — opens background modal directly without collapsing VTO
  const handleDirectBackgroundModalOpen = useCallback(() => {
    setIsBackgroundModalOpen(true);
  }, [setIsBackgroundModalOpen]);

  const handleToggleControlsCollapse = useCallback(() => {
    setIsControlsCollapsed(prev => !prev);
  }, []);

  return {
    // Panel collapse states
    isVTOPanelCollapsed,
    setIsVTOPanelCollapsed,
    isManualPanelCollapsed,
    setIsManualPanelCollapsed,
    isControlsCollapsed,
    setIsControlsCollapsed,
    handleToggleControlsCollapse,

    // Modal open handlers (with panel collapse logic)
    handlePersonImageModalOpen,
    handleBackgroundModalOpen,
    handleLoginRequired,
    handleVTOWearerClick,
    handleDirectPersonModalOpen,
    handleDirectBackgroundModalOpen
  };
}

export default useMyClosetPanels;
